import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  LinearProgress,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Box from '@mui/system/Box'
import SearchResultNotify from 'app/components/SearchResultNotify'
import {useRunNotifySearch} from 'app/hooks/searches/useRunNotifySearch'
import {useAuthProvider} from 'app/modules/auth'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import React from 'react'
import CardHeader from 'react-bootstrap/esm/CardHeader'
import {useDispatch, useSelector} from 'react-redux'
import {useSearchParams} from 'react-router-dom'
import {fetchSavedSearches} from 'redux/actions/savedSearchActions'
import {selectSavedSearchSlice} from 'redux/selectors/savedSearchSelector'
import {AppDispatch} from 'redux/store'
import DiscoveryDescribeRequirementsNotifySearch from './discovery-detail/DiscoveryDescribeRequirementsNotifySearch'

export default function NotificationSearchList(): React.ReactElement {
  const {reduxUser} = useAuthProvider()
  const dispatch = useDispatch<AppDispatch>()
  const savedSearchRedux = useSelector(selectSavedSearchSlice)
  const isLoading = savedSearchRedux.status === 'loading'
  const [searchParams] = useSearchParams()
  const scrollPosition = Number(searchParams.get('scrollPosition'))
  const [showAddSearchDialog, setShowAddSearchDialog] = React.useState<boolean>(false)
  const [editSearch, setEditSearch] = React.useState<ISavedSearchFE>()
  const exeRunNotify = useRunNotifySearch()

  const handleSetEditSearch = (es?: ISavedSearchFE, show?: boolean) => {
    setEditSearch(es)
    show && setShowAddSearchDialog(show)
  }

  const handleCloseDialog = () => {
    setEditSearch(undefined)
    setShowAddSearchDialog(false)
  }

  const handleAddSearch = (show: boolean) => {
    if (notifyCount < 1) {
      setEditSearch(undefined)
      show && setShowAddSearchDialog(show)
    } else {
      alert(
        'Limit reached. You already have a saved search for notifications. Please edit that one or delete it before adding a new one.'
      )
      setShowAddSearchDialog(false)
      setEditSearch(undefined)
    }
  }

  React.useEffect(() => {
    if (!isLoading && scrollPosition && scrollPosition !== 0) {
      window.scrollTo(0, scrollPosition)
    }
  }, [scrollPosition])

  React.useEffect(() => {
    if (!savedSearchRedux.savedNotifySearches) {
      dispatch(fetchSavedSearches())
    }
  }, [savedSearchRedux.savedRecentSearches])

  const notifyCount = savedSearchRedux?.savedNotifySearches?.length ?? 0

  const handleRunNotifications = async () => {
    const x = await exeRunNotify.mutateAsync({
      user_id: reduxUser?.id ?? '',
      user_email: reduxUser?.email ?? '',
      graph_entity_type: 'opportunity',
      notify_entity_type: 'opportunities',
    })
    if (x) {
      console.log('Notifications run successfully:', x)
    }
  }

  const [scroll, setScroll] = React.useState<DialogProps['scroll']>('paper')

  // The concept weights and ignoring only applies to the redux state and so resetting the in state editing search is needed with useEffect to update in the dialog and on the list
  React.useEffect(() => {
    if (editSearch?.id && savedSearchRedux.savedNotifySearches) {
      const foundSearch = savedSearchRedux.savedNotifySearches.find(
        (search: ISavedSearchFE) => search.id === editSearch.id
      )
      if (foundSearch) {
        setEditSearch(foundSearch)
      }
    }
  }, [editSearch, showAddSearchDialog, setEditSearch, savedSearchRedux])

  return (
    <>
      <Dialog
        open={showAddSearchDialog}
        onClose={() => handleCloseDialog()}
        scroll={scroll}
        maxWidth={'lg'}
        fullWidth
      >
        <DialogTitle
          id='scroll-dialog-title'
          sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Typography variant={'h5'}>{`${
            editSearch ? 'Edit' : 'Add'
          } a search for notifications`}</Typography>
          <IconButton
            edge='start'
            color='inherit'
            onClick={() => handleCloseDialog()}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DiscoveryDescribeRequirementsNotifySearch
            savedSearch={editSearch ?? ({search_text: ''} as ISavedSearchFE)}
            setSavedSearch={setEditSearch}
            showAddSearchDialog={showAddSearchDialog}
            handleCloseDialog={() => handleCloseDialog()}
          />
        </DialogContent>
      </Dialog>

      <Card elevation={0}>
        <CardHeader>
          <>
            <Typography variant={'h5'} mt={3} ml={5} mb={2}>
              Notification Searches
            </Typography>
            {/* <Button
              variant={'contained'}
              color={'primary'}
              onClick={() => handleRunNotifications()}
            >
              Run The Notifications
            </Button> */}
            <Box display={'flex'} justifyContent={'flex-end'} mr={4} mb={1}>
              <Box>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  onClick={() => handleAddSearch(true)}
                >
                  <Box display={'flex'} justifyItems={'center'} alignItems={'center'}>
                    <AddIcon fontSize='small' />
                    <Typography variant={'button'}>Add Search For Notifications</Typography>
                  </Box>
                </Button>
              </Box>
            </Box>
          </>
        </CardHeader>

        <CardContent>
          {savedSearchRedux?.savedNotifySearches ? (
            <Box m={2}>
              <SearchResultNotify
                searchResults={savedSearchRedux?.savedNotifySearches ?? undefined}
                loading={isLoading}
                handleEditSearch={handleSetEditSearch}
              />
            </Box>
          ) : (
            <LinearProgress />
          )}
        </CardContent>
      </Card>
    </>
  )
}
